import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getCollectionBySlug, collectionBanner, filterProducts, COLLECTIONS } from "../lib/store";
import ProductCard from "../components/ProductCard";
import Seo, { breadcrumbSchema, itemListSchema } from "../components/Seo";
import { CreditNotice } from "../components/CreditNotice";
import { trackViewItemList } from "../lib/analytics";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const PER_PAGE = 24;

export default function CollectionPage() {
  const { slug } = useParams();
  const collection = getCollectionBySlug(slug);
  const [sort, setSort] = useState("featured");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); window.scrollTo(0, 0); }, [slug, sort]);

  useEffect(() => { if (collection) trackViewItemList(collection.title, 0); }, [collection]);

  const results = useMemo(
    () => (collection ? filterProducts({ ...collection.filter, sort }) : []),
    [collection, sort]
  );

  if (!collection) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-3xl text-[#1a1713]">Collection Not Found</h1>
        <Link to="/collections"><Button className="mt-6 rounded-none bg-[#1a1713] text-[#f3ead2] hover:bg-[#3a2f1d]">View All Collections</Button></Link>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(results.length / PER_PAGE));
  const pageItems = results.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const others = COLLECTIONS.filter((c) => c.slug !== slug).slice(0, 5);

  return (
    <div>
      <Seo
        title={`${collection.title} Jewelry in Chicago`}
        description={`${collection.blurb} Browse ${results.length} pieces at Regal Jewels, Chicago. Inquire online, on WhatsApp, or book a private visit.`}
        path={`/collections/${collection.slug}`}
        image={collectionBanner(collection)}
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Collections", path: "/collections" },
            { name: collection.title, path: `/collections/${collection.slug}` },
          ]),
          itemListSchema(pageItems, `/collections/${collection.slug}`),
        ]}
      />
      {/* Cinematic banner */}
      <section className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <img src={collectionBanner(collection)} alt={collection.title} className={`absolute inset-0 w-full object-cover bg-[#f4efe5] ${collection.bannerPos || ""} ${collection.slug === "gold" ? "h-[160%] sm:h-full" : "h-full"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1713]/75 via-[#1a1713]/20 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-end pb-12">
          <div>
            <p className="text-[#e9d9ac] tracking-[0.4em] uppercase text-[10px] mb-3">
              <Link to="/" className="hover:text-white transition-colors">Home</Link> / <Link to="/collections" className="hover:text-white transition-colors">Collections</Link>
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl text-white">{collection.title}</h1>
            <p className="text-white/85 mt-3 max-w-xl text-sm sm:text-base leading-relaxed">{collection.blurb}</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-sm text-[#8f8571]">{results.length} pieces</p>
            <CreditNotice placement="catalog" className="mt-2" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] rounded-none border-[#d8cdb5] bg-white">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {pageItems.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-heading text-2xl text-[#3a352c]">No pieces in this collection yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
            {pageItems.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-14">
            <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo(0, 0); }} className="rounded-none border-[#d8cdb5]">Previous</Button>
            <span className="text-sm text-[#5c5240] px-4">Page {page} of {totalPages}</span>
            <Button variant="outline" disabled={page === totalPages} onClick={() => { setPage(page + 1); window.scrollTo(0, 0); }} className="rounded-none border-[#d8cdb5]">Next</Button>
          </div>
        )}

        {/* Other collections */}
        <div className="border-t border-[#eae2d0] mt-20 pt-10 pb-2">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8f8571]">More Collections:</span>
            {others.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} className="editorial-link">
                {c.title} <ArrowRight size={11} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
